import { create } from "zustand";
import { OptionValueType } from "../enums";
import { DraggableItem } from "../components/draggableItem/types";
import { Item } from "../../../../../../../../../utils/types/generals";
import { Priority } from "../../../../../../../../../utils/enums/priority";


interface ExpansionRecord {
  itemId: string;
  parentId: string | null;
  level: number;
  timestamp: number;
  label: string;
}

interface CatalogState {
  subValueTypes: { label: string, value: number }[];
  setSubValueTypes: (values: { label: string, value: number }[]) => void;
  subCatalog: { label: string, value: number, children:{ id:number, name:string }[] }[];
  setSubCatalog: (values: { label: string, value: number, children:{ id:number, name:string }[] }[]) => void;

  //CRUD elementos del arbol
  items: DraggableItem[];
  setItems: (items: DraggableItem[]) => void;
  itemsFromEndpoint: DraggableItem[];
  setItemsFromEndpoint: (items: DraggableItem[]) => void;
  addItem: (parentId: string | null, item: DraggableItem | DraggableItem[]) => void;
  editItem: (id: string, label?: string, priority?: Priority, provider?: number, textInput?: boolean) => void;
  editItemSubCatalog: (id: string, subCatalog?:number) => void;
  deleteItem: (id: string) => void;
  moveItem: (parentId: string | null, reordered: DraggableItem[]) => void;
  selectedItemRoot: DraggableItem | null;
  setSelectedItemRoot: (item: DraggableItem | null) => void;
  selectedItem: { [parentId: string]: DraggableItem | null };
  setSelectedItem: (parentId: string, item: DraggableItem | null) => void;
  clearChildren: (parentId: string | null) => void;

  //Provider
  providers?: Item[];
  setProviders: (values: Item[]) => void;
  assignProvider: (providerId: number | undefined, parentId: string | null, itemId: string) => void

  // Control de expansión de ramas
  setExpandedLevel: ( itemId: string ) => void;
  collapseItemAndChildren: (rootBranchId: string, itemId: string) => void;
}


export const useCatalogStoreModule = create<CatalogState>((set, get) => ({

  subValueTypes: [{ label: "Ingresar valor", value: OptionValueType.VALUE }, { label: "Seleccionar subcatálogo", value: OptionValueType.SUB_CATALOG }],
  setSubValueTypes: (values) => set({ subValueTypes: values }),
  subCatalog: [],
  setSubCatalog: (values) => set({ subCatalog: values }),

  //Providers
  providers: [],
  setProviders: (values) => set({ providers: values }),

  //CRUD elementos del arbol
  items: [],
  setItems: (items) => set({ items }),

  itemsFromEndpoint: [],
  setItemsFromEndpoint: (itemsFromEndpoint) => set({ itemsFromEndpoint }),

  addItem: (parentId, newItems) => {
    const itemsToAdd = Array.isArray(newItems) ? newItems : [newItems];

    const addRecursive = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) =>
        item.id === parentId
          ? {
            ...item,
            children: [...(item.children || []), ...itemsToAdd],
          }
          : {
            ...item,
            children: item.children ? addRecursive(item.children) : [],
          }
      );

    const currentItems = get().items;

    if (parentId) {
      set({ items: addRecursive(currentItems) });
    } else {
      set({ items: [...currentItems, ...itemsToAdd] });
    }

  },

  editItem: (id, label, priority, provider, textInput) => {
    const editRecursive = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) =>
        item.id === id
          ? {
            ...item,
            label: label ?? item.label,
            priority: priority ?? item.priority,
            provider: provider /* ?? item.provider */,
            textInput: textInput /* ?? item.textInput */,
          }
          : { ...item, children: item.children ? editRecursive(item.children) : [] }
      );

    set({ items: editRecursive(get().items) });
  },

  editItemSubCatalog: (id, subCatalog) => {
    const editRecursive = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) =>
        item.id === id
          ? {
            ...item,
            subcatalog: subCatalog
          }
          : { ...item, children: item.children ? editRecursive(item.children) : [] }
      );

    set({ items: editRecursive(get().items) });
  },

  deleteItem: (id: string) => {
    set((state) => {
      //Eliminar el item del árbol de items
      const deleteRecursive = (list: DraggableItem[]): DraggableItem[] =>
        list
          .filter((item) => item.id !== id)
          .map((item) => ({
            ...item,
            children: item.children ? deleteRecursive(item.children) : [],
          }));

      const updatedItems = deleteRecursive(state.items);

      const isSelectedDeleted =
        state.selectedItemRoot?.id === id;

      const updatedSelectedItems = Object.fromEntries(
        Object.entries(state.selectedItem).filter(
          ([, selected]) => selected?.id !== id // eliminar solo el que coincide
        )
      );

      return {
        ...state,
        items: updatedItems,
        selectedItem: updatedSelectedItems,
        selectedItemRoot: isSelectedDeleted ? null : state.selectedItemRoot,
      };
    });

  },

  moveItem: (parentId, reordered) => {
    if (!parentId) {
      set({ items: reordered });
      return;
    }

    const moveRecursive = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) =>
        item.id === parentId
          ? { ...item, children: reordered }
          : { ...item, children: item.children ? moveRecursive(item.children) : [] }
      );

    set({ items: moveRecursive(get().items) });
  },

  assignProvider: (providerId: number | undefined, parentId: string | null, itemId: string) => {
    // Si parentId tiene valor: asignar al item especificado
    if (parentId) {
      const updateAssignToItem = (list: DraggableItem[]): DraggableItem[] =>
        list.map((item) =>
          item.id === itemId
            ? { ...item, provider: providerId }
            : { ...item, children: item.children ? updateAssignToItem(item.children) : [] }
        );

      set({ items: updateAssignToItem(get().items) });
      return;
    }

    // Si parentId es null: asignar SOLO a los hijos directos del item identificado por itemId
    const updateAssignToDirectChildren = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) => {
        if (item.id === itemId) {
          const newChildren = (item.children || []).map((child) => ({
            ...child,
            provider: providerId,
            // No tocar sus hijos (nietos): conservar child.children tal cual
            children: child.children ?? [],
          }));

          return { ...item, children: newChildren };
        }

        return {
          ...item,
          children: item.children ? updateAssignToDirectChildren(item.children) : [],
        };
      });

    set({ items: updateAssignToDirectChildren(get().items) });
  },


  clearChildren: (parentId) => {
    if (parentId) {
      const clearRecursive = (list: DraggableItem[]): DraggableItem[] =>
        list.map((item) =>
          item.id === parentId
            ? { ...item, children: [] }
            : { ...item, children: item.children ? clearRecursive(item.children) : [] }
        );

      set({ items: clearRecursive(get().items) });
    } else {
      set({ items: [] });
    }
  },

  selectedItemRoot: null,
  setSelectedItemRoot: (item) => set({ selectedItemRoot: item }),

  selectedItem: {},
  setSelectedItem: (parentId, item) =>
    set((state) => ({
      selectedItem: {
        ...state.selectedItem,
        [parentId]: item,
      },
    })),

  setExpandedLevel: ( itemId: string ) => {
    set((state) => {
      //Colapsar todo un subárbol
      const collapseAll = (item: DraggableItem): DraggableItem => ({
        ...item,
        expand: false,
        children: item.children.map(collapseAll),
      });

      //Buscar nodo y actualizar expand
      const updateExpandState = (items: DraggableItem[]): DraggableItem[] =>
        items.map(item => {
          if (item.id === itemId) {
            const isExpanding = !item.expand;

            // Si se está colapsando, cerrar recursivamente a todos los hijos
            return isExpanding
              ? { ...item, expand: true }
              : collapseAll({ ...item, expand: false });
          }

          // Recursividad en hijos
          return { ...item, children: updateExpandState(item.children) };
        });

      const updatedItems = updateExpandState(state.items);

      return {
        ...state,
        items: updatedItems,
      };
    });
  },

  collapseItemAndChildren: (rootBranchId: string, itemId: string) => {
    set((state) => {
      //Función para colapsar todo un subárbol
      const collapseAll = (item: DraggableItem): DraggableItem => ({
        ...item,
        expand: false,
        children: item.children.map(collapseAll),
      });

      //Buscar y colapsar el ítem dentro del árbol
      const collapseRecursive = (items: DraggableItem[]): DraggableItem[] =>
        items.map((item) => {
          if (item.id === itemId) {
            return collapseAll(item);
          }
          return {
            ...item,
            children: item.children ? collapseRecursive(item.children) : [],
          };
        });

      const updatedItems = collapseRecursive(state.items);

      return {
        ...state,
        items: updatedItems,
      };
    });
  },


}));